<script setup lang="ts">
    import { useHipSpineAnalysisViewModel } from '@/views/3d-planner/hipSpine/useHipSpineAnalysisViewModel';
    import GroupLabel from '@/views/3d-planner/components/GroupLabel.vue';
    import HipSpineNomogram from '@/views/3d-planner/components/HipSpineNomogram.vue';
    import SurgeonDefinedBounds from './SurgeonDefinedBounds.vue';
    import interceptIcon from '@/assets/icons/intercept.svg';
    import { HIP_SPINE_TARGETS } from '@/domain/analytics/analyticsTargets';

    const vm = useHipSpineAnalysisViewModel();
</script>

<template>
    <div
        v-if="vm.safeZoneVisibility === 'show'"
        class="hip-spine-panel-wrapper d-flex flex-column mr-3"
    >
        <!-- Mirrors the cup column's title-row line-height + my-2 pt-2 so the
             hip-spine card top aligns with the cup card. -->
        <div class="text-paragraph-sm my-2 pt-2" aria-hidden="true">&nbsp;</div>

        <v-sheet
            class="hip-spine-analysis-container d-flex flex-column bg-surface-light mt-2 flex-grow-1"
            :data-analytics-id="HIP_SPINE_TARGETS.hipSpineAnalysis"
        >
            <div class="hip-spine-analysis-container-form py-4 px-6">
                <group-label>Hip-Spine Analysis</group-label>
                <p class="text-secondary-text mt-2">
                    Adjust the cup Anteversion and Inclination to change the marker in the safe
                    zone.
                </p>

                <!-- Nomogram chart -->
                <div class="nomogram-section my-8">
                    <v-progress-linear
                        v-if="vm.isLoading"
                        indeterminate
                        color="primary"
                        class="my-2"
                    />
                    <template v-else-if="vm.nomogramData">
                        <hip-spine-nomogram
                            :nomogram-data="vm.nomogramData"
                            :data-analytics-id="HIP_SPINE_TARGETS.nomogram"
                        />
                    </template>
                </div>
            </div>

            <div
                class="px-3 py-3 mb-2 flex-shrink-0"
                :data-analytics-id="HIP_SPINE_TARGETS.boundsControls"
            >
                <!-- Edit Nomogram button / Surgeon Defined Bounds panel -->
                <div
                    :class="
                        vm.isEditingBounds ? 'bounds-container-open' : 'bounds-container-closed'
                    "
                    class="text-right"
                >
                    <surgeon-defined-bounds
                        v-if="vm.isEditingBounds"
                        :bounds="vm.editableBounds"
                        :inclination-lower-max="vm.inclinationLowerMax"
                        :inclination-upper-min="vm.inclinationUpperMin"
                        :anteversion-lower-max="vm.anteversionLowerMax"
                        :anteversion-upper-min="vm.anteversionUpperMin"
                        @update:bound="vm.updateBound"
                        @close="vm.stopEditing"
                    />
                    <app-button-secondary
                        v-else
                        :disabled="!vm.canEditBounds"
                        @click="vm.startEditing"
                    >
                        Edit Bounds
                    </app-button-secondary>
                </div>

                <!-- Divider between bounds and legend (only when editing) -->
                <v-divider v-if="vm.isEditingBounds" class="mx-auto" style="width: 90%" />

                <!-- Legend -->
                <div
                    :class="vm.isEditingBounds ? 'legend-container-open' : ''"
                    class="legend-container border-sm rounded-lg"
                >
                    <div class="legend-grid pa-2">
                        <div class="d-flex align-center ga-2">
                            <span class="legend-swatch legend-swatch-safe-zone" />
                            <span>CSI Bounds (safe zone)</span>
                        </div>
                        <div class="d-flex align-center ga-2">
                            <img
                                :src="interceptIcon"
                                alt="Inclination-Anteversion Target Icon"
                                width="19px"
                            />
                            <span>Inclination/Anteversion Targets</span>
                        </div>
                        <div class="d-flex align-center ga-2">
                            <span class="legend-swatch legend-swatch-angle-ranges" />
                            <span>Surgeon Defined Bounds</span>
                        </div>
                        <div class="d-flex align-center ga-2">
                            <span class="legend-line legend-line-combined-version" />
                            <span>Combined Version</span>
                        </div>
                    </div>
                </div>
            </div>
        </v-sheet>
    </div>
</template>

<style scoped lang="scss">
    // Bottom margin mirrors the approve-plan-footer's `top: -1vh` lift in the
    // adjacent column so the two card bottoms align. min-height: 0 lets the
    // wrapper shrink below its intrinsic content height when the row's
    // available height is short, so the scroll burden lands on the form
    // inside, not on the outer drawer.
    .hip-spine-panel-wrapper {
        margin-bottom: 1vh;
        min-height: 0;
    }

    .hip-spine-analysis-container {
        background: none;
        width: 400px;
        border-radius: 12px;
        // Same reason as the wrapper: allow shrinking below intrinsic content.
        min-height: 0;
    }

    .hip-spine-analysis-container-form {
        // flex: 1 + min-height: 0 lets the form absorb the leftover height
        // inside the sheet (after the pinned bottom block) and crucially
        // lets it shrink below its intrinsic content height so overflow-y
        // can take over when the Surgeon Defined Bounds editor expands the
        // bottom block.
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
    }

    .nomogram-section {
        flex: 1 1 auto;
        min-height: 150px;
        display: flex;
        flex-direction: column;
    }

    .legend-grid {
        display: grid;
        grid-template-columns: 2fr 3fr;
        gap: 12px 12px;
        white-space: nowrap;
    }

    .legend-swatch {
        display: inline-block;
        width: 16px;
        height: 16px;
        border-radius: 3px;
        flex-shrink: 0;
    }

    .legend-swatch-safe-zone {
        background-color: rgba(var(--v-theme-green));
    }

    .legend-swatch-angle-ranges {
        background-color: rgba(var(--v-theme-grey-light-1));
    }

    .legend-line {
        display: inline-block;
        width: 20px;
        height: 0;
        flex-shrink: 0;
    }

    .legend-line-combined-version {
        border-top: 2px solid;
        border-color: rgb(var(--v-theme-primary));
    }

    .bounds-container-closed {
        margin-bottom: 16px;
    }

    .bounds-container-open {
        :deep(.surgeon-defined-bounds) {
            border-bottom: none !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }
    }

    .legend-container {
        font-size: 11px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.4px;
    }

    .legend-container-open {
        border-top: none !important;
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
    }

    @media screen and (max-width: 820px) {
        .hip-spine-analysis-container {
            width: 330px;
        }
    }
</style>
